import { clearManualFootageStorage } from "./manualFootageStorage";
import { clientStore } from "./clientStorage";
import {
  ACTIVE_PERSISTENCE_USER_KEY,
  advancePersistenceGeneration,
  getActivePersistenceUser,
  persistenceGenerationKey,
  PERSISTENCE_LOCAL_CLEAR_KEY,
} from "./persistenceIdentity";
import {
  classifyPersistenceKey,
  isActiveUserPayloadKey,
  LEGACY_LOCAL_DATA_KEYS,
} from "./persistenceRegistry";
import { walkthroughSessionStore } from "./walkthroughSessionStore";

export interface LocalDataClearReceipt {
  receiptId: string;
  userId: string;
  generation: number;
  removedCount: number;
  removedByCategory: Record<string, number>;
  unregisteredRemoved: string[];
  failedKeys: string[];
  retainedTheme: boolean;
  retainedAuthKeyCount: number;
  retainedOtherUserKeyCount: number;
  coordinationKeyCount: number;
  coordinationVerified: boolean;
  enumerationVerified: boolean;
  durability: "durable" | "memory-only";
  complete: boolean;
}

const isSupabaseAuthKey = (key: string) =>
  /^sb-[a-z0-9-]+-auth-token$/i.test(key);

const isOtherUserPayloadKey = (key: string, activeUserId: string) =>
  /^(persist|search_draft|search_result|scroll):/.test(key) &&
  !isActiveUserPayloadKey(key, activeUserId);

/**
 * Clears only LocateNowPro-owned active-user payload. Coordination metadata is
 * advanced/preserved so already queued writers cannot resurrect deleted state.
 * Supabase auth, device-global theme, and other users' namespaced payloads are
 * explicit non-targets.
 */
export function clearActiveUserLocalData(userId: string): LocalDataClearReceipt {
  if (!userId || getActivePersistenceUser() !== userId) {
    throw new Error("Clear Local Data requires the active authenticated identity");
  }

  const beforeEnumeration = clientStore.enumerate();
  const before = beforeEnumeration.keys;
  const walkthroughBefore = walkthroughSessionStore.read();
  const hadWalkthroughSessionValue = walkthroughBefore.completed;
  const targetKeys = before.filter(
    (key) =>
      isActiveUserPayloadKey(key, userId) ||
      (LEGACY_LOCAL_DATA_KEYS as readonly string[]).includes(key)
  );
  const removedByCategory: Record<string, number> = {};
  const unregisteredRemoved: string[] = [];
  const failedKeys: string[] = [];
  if (!beforeEnumeration.complete) {
    failedKeys.push("storage:enumeration-before");
  }

  // Fence all mounted and in-flight writers before deleting any payload.
  const generation = advancePersistenceGeneration(userId);
  const clearEventRaw = JSON.stringify({
    userId,
    generation,
    at: Date.now(),
  });
  clientStore.setItem(PERSISTENCE_LOCAL_CLEAR_KEY, clearEventRaw);

  for (const key of targetKeys) {
    const policy = classifyPersistenceKey(key, userId);
    const category = policy?.category ?? "unregistered";
    if (!policy) unregisteredRemoved.push(key);
    const removed = clientStore.removeItem(key);
    if (removed && clientStore.getItem(key) === null) {
      removedByCategory[category] = (removedByCategory[category] ?? 0) + 1;
    } else {
      failedKeys.push(key);
    }
  }

  // This session-only UX marker is LocateNowPro local data, but not durable.
  if (!walkthroughBefore.verified || !walkthroughSessionStore.clear()) {
    failedKeys.push("session:demo_walkthrough_completed");
  } else if (hadWalkthroughSessionValue) {
    removedByCategory.workspace = (removedByCategory.workspace ?? 0) + 1;
  }

  const footage = clearManualFootageStorage(userId);
  if (!footage.cleared) failedKeys.push("session:manual-footage");
  else if (footage.removed) removedByCategory.draft = (removedByCategory.draft ?? 0) + footage.removed;

  // Publish the same active identity again after advancing its generation.
  // Mounted persistence hooks observe this and reset their memory mirrors.
  clientStore.setItem(ACTIVE_PERSISTENCE_USER_KEY, userId);
  clientStore.clearDurabilityFailures(
    (key) =>
      isActiveUserPayloadKey(key, userId) ||
      (LEGACY_LOCAL_DATA_KEYS as readonly string[]).includes(key)
  );

  const afterEnumeration = clientStore.enumerate();
  if (!afterEnumeration.complete) {
    failedKeys.push("storage:enumeration-after");
  }
  const remainingActivePayload = afterEnumeration.keys
    .filter((key) => isActiveUserPayloadKey(key, userId));
  for (const key of remainingActivePayload) {
    if (!failedKeys.includes(key)) failedKeys.push(key);
  }

  const after = afterEnumeration.keys;
  const status = clientStore.durability();
  const durableClearEvent = clientStore.getDurableItem(
    PERSISTENCE_LOCAL_CLEAR_KEY
  );
  const coordinationVerified =
    clientStore.getDurableItem(persistenceGenerationKey(userId)) ===
      String(generation) &&
    durableClearEvent === clearEventRaw &&
    clientStore.getDurableItem(ACTIVE_PERSISTENCE_USER_KEY) === userId;
  if (!coordinationVerified) {
    failedKeys.push("coordination:persistence-fence");
  }
  const receiptId = `local-clear-${userId}-${generation}-${Date.now()}`;
  return {
    receiptId,
    userId,
    generation,
    removedCount: Object.values(removedByCategory).reduce(
      (total, count) => total + count,
      0
    ),
    removedByCategory,
    unregisteredRemoved,
    failedKeys,
    retainedTheme: after.includes("lnpro.themeMode"),
    retainedAuthKeyCount: after.filter(isSupabaseAuthKey).length,
    retainedOtherUserKeyCount: after.filter((key) =>
      isOtherUserPayloadKey(key, userId)
    ).length,
    coordinationKeyCount: after.filter((key) =>
      key.startsWith("ln:persistence:")
    ).length,
    coordinationVerified,
    enumerationVerified:
      beforeEnumeration.complete && afterEnumeration.complete,
    durability: status.mode,
    complete:
      failedKeys.length === 0 &&
      remainingActivePayload.length === 0 &&
      beforeEnumeration.complete &&
      afterEnumeration.complete &&
      coordinationVerified,
  };
}
